import { useNavigate } from "@tanstack/react-router";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { localeAtom } from "#/stores/locale-store.ts";
import { resolvedThemeAtom, themeHydratedAtom } from "#/stores/theme-store.ts";
import { useChartTradeLines } from "../../hooks/use-chart-trade-lines.ts";
import { useTradeMarket } from "../../hooks/use-trade-data.ts";
import { TradePanel } from "../trade-panel.tsx";
import { ChartError } from "./chart-error.tsx";
import { ChartLoader } from "./chart-loader.tsx";
import { useChartSymbolSync } from "./hooks/use-chart-symbol-sync.ts";
import { useTradingViewWidget } from "./hooks/use-trading-view-widget.ts";

const MOBILE_CHART_USER_AGENT_PATTERN =
	/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

function getIsMobileChart() {
	if (typeof navigator === "undefined") return false;

	const userAgent = navigator.userAgent;
	const isIpadOsDesktopMode =
		navigator.maxTouchPoints > 1 && /\bMacintosh\b/.test(userAgent);

	return MOBILE_CHART_USER_AGENT_PATTERN.test(userAgent) || isIpadOsDesktopMode;
}

export function Chart() {
	const navigate = useNavigate();
	const { marketData } = useTradeMarket();
	const locale = useAtomValue(localeAtom);
	const hasHydrated = useAtomValue(themeHydratedAtom);
	const resolvedTheme = useAtomValue(resolvedThemeAtom);
	const [isMobileChart] = useState(getIsMobileChart);

	const handleChartSymbolChanged = useCallback(
		(symbol: string) => {
			if (symbol === marketData.chartSymbol) {
				return;
			}

			void navigate({
				to: "/trade/$symbol",
				params: { symbol },
			});
		},
		[marketData.chartSymbol, navigate],
	);

	const {
		containerRef,
		widgetRef,
		chartReadyRef,
		currentSymbolRef,
		isWidgetReady,
		loadError,
		retryLoad,
		handleLoadError,
		handleLoadSuccess,
	} = useTradingViewWidget({
		hasHydrated,
		isMobile: isMobileChart,
		locale,
		onChartSymbolChanged: handleChartSymbolChanged,
		requestedSymbol: marketData.chartSymbol,
		resolvedTheme,
	});

	const { isSwitchingSymbol } = useChartSymbolSync({
		chartReadyRef,
		currentSymbolRef,
		isMobile: isMobileChart,
		nextSymbol: marketData.chartSymbol,
		onError: handleLoadError,
		onSuccess: handleLoadSuccess,
		resolvedTheme,
		widgetRef,
	});

	const isReady = isWidgetReady && !isSwitchingSymbol;

	useChartTradeLines(widgetRef, isReady);

	return (
		<TradePanel className="overflow-hidden border-trade-border-subtle">
			<div className="trade-panel-handle h-1.5 shrink-0 cursor-grab" />
			<div className="relative min-h-0 flex-1">
				<div ref={containerRef} className="trade-tv h-full w-full" />
				<ChartLoader isReady={isReady} />
				{loadError && <ChartError onRetry={retryLoad} />}
			</div>
		</TradePanel>
	);
}
